import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';

export const DEBOUNCE_DELAY_MS = 5000;
export const RETRY_DELAY_MS = 200;

export const LATEST_TOKEN_VALID_DATE = 30;

// How far ahead of the access token's `exp` the proactive refresh is scheduled. Deliberately SEPARATE
// from LATEST_TOKEN_VALID_DATE, which is the shared validity predicate gating hasResumableSession() and
// the native boot/resume session clearing — moving that would change all three.
export const PROACTIVE_REFRESH_LEAD_TIME_SECONDS = 15;

// setTimeout clamps to a 32-bit signed millisecond range, so a long-lived token has to be woken up in
// chunks rather than scheduled in one go.
export const MAX_REFRESH_TIMEOUT_MS = 2147483647;

// A tab that crashes mid-refresh leaves IS_REFRESHING_IN_OTHER_TAB set. Bound the wait so the survivors
// fall through and refresh themselves instead of polling forever.
export const REFRESH_IN_OTHER_TAB_MAX_ATTEMPTS = 25;

// Emitted by AccountsAuthenticationHandler (DEV-6375) on a 401 whose inbound token was already past `exp`.
// Matched on the DESCRIPTION, not the bare `error="invalid_token"` code — RFC 6750 reuses that code for
// revoked / malformed / wrong-audience / bad-signature tokens too, and those are recoverable sessions.
export const EXPIRED_TOKEN_CHALLENGE_MARKER = 'The access token expired';

// Tabs all read the same `exp` from shared storage, so an un-jittered schedule wakes them together and they
// contend on a localStorage flag that has no compare-and-set. Kept well under the lead time so the refresh
// still lands ahead of expiry.
export const REFRESH_SCHEDULE_JITTER_MS = 5000;

// A refresh rejected with one of these means the session cannot be recovered by retrying. 400 and 403 are
// the two the refresh endpoint actually declares (TokensController: InvalidToken / TokenExpired /
// InvalidData, and NoPermissionToUseThisAuthScope) — a 403 there is permanent, so treating it as transient
// would park the user in a session that 401s on every request and 403s on every refresh, forever. 401/404
// are kept because the pre-existing DEV-4332 interceptor already treated them as fatal on the refresh route.
// Anything else — a network error, a lost cross-tab rotation race — is transient and must NOT wipe the
// tab-shared token storage.
export const REFRESH_FATAL_STATUSES = [
  HTTP_RESPONSE_STATUS_CODES.BAD_REQUEST,
  HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED,
  HTTP_RESPONSE_STATUS_CODES.FORBIDDEN,
  HTTP_RESPONSE_STATUS_CODES.NOT_FOUND,
];

// A transient failure keeps the session, so the cycle must re-arm itself or the tab silently loses its
// schedule for the rest of the session. The normal schedule clamps to ~now for an already-stale token, so
// retries back off from RETRY_DELAY_MS instead of hammering the endpoint — 200/400/800/1600/3200/6400ms,
// 12.6s in total, comfortably inside the lead time — then hand back to the wake events.
export const REFRESH_RETRY_MAX_ATTEMPTS = 6;
